import { useEffect, useState } from 'react'
import { useLocation } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import type { NavCountKey } from './navigation'

/**
 * The live numbers the rail prints beside its sections — SHELL-1 and SHELL-2.
 *
 * `design/screens/shell.html:69-73` draws *Sites 2*, *Keywords 148*, *Technical
 * audit 7*, *Content 5* and a warning badge reading *3* on Approvals, and the
 * screen's own note says the badge is *"the one thing a tenant opens their phone
 * for"* — so it is drawn on the mobile bar as well as in the sidebar.
 *
 * **Read once in `AppShell` and passed down**, rather than by each rail that
 * draws them. The sidebar renders twice below 1100px — the fixed rail and the
 * drawer — and the mobile bar is a third caller; three hooks would be three
 * requests for one answer.
 *
 * **Re-read on every navigation, and that is the whole point of the badge.**
 * `AppShell` is a layout route: it mounts once and survives every child
 * navigation, so a query with no dependencies would fire on sign-in and never
 * again — the rail would still say *Approvals 3* after the tenant approved all
 * three, until they reloaded the page. The pathname is what makes it honest, and
 * the endpoint was built for exactly this shape: five `count(*)` on one
 * transaction, loading no rows.
 *
 * **The last good answer is held across the refetch.** `useQuery` returns to
 * `loading` whenever its dependencies change, so returning its state directly
 * would blank every count for the length of one request on every navigation —
 * which is the flicker `BudgetMeter` refuses a skeleton to avoid. Holding the
 * previous numbers means the rail changes when the numbers do and at no other
 * time. A failed refetch keeps them for the same reason: a sidebar is not where
 * a failed request is reported, and last-known beats nothing.
 *
 * **`null` until the first answer, and `null` renders as no counts at all** —
 * the rail as it was drawn before SHELL-1. Loading, a failed first request and a
 * tenant the endpoint refuses all reach it the same way.
 */
export type NavCounts = Schema<'NavCountsOut'>

export function useNavCounts(): NavCounts | null {
  const { pathname } = useLocation()
  const counts = useQuery(
    (signal) => call(() => api.GET('/api/v1/nav/counts', { signal })),
    [pathname],
  )
  const [retained, setRetained] = useState<NavCounts | null>(null)

  useEffect(() => {
    if (counts.state.status === 'ready') setRetained(counts.state.data)
  }, [counts.state])

  return retained
}

/**
 * What to print after a section's label, or `null` for nothing.
 *
 * **Zero prints nothing**, deliberately. A rail reading *Approvals 0* is a
 * badge that has to be read to learn there is nothing to read, and
 * `shell.html` draws a count on four of its eight entries rather than on all
 * eight — a section with nothing in it is quiet.
 */
export function countFor(counts: NavCounts | null, key: NavCountKey | undefined): number | null {
  if (counts === null || key === undefined) return null
  const value = counts[key]
  return value > 0 ? value : null
}
